// MP266 — Client helpers for the org-scoped agent approval queue.

import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface Approval {
  id: string;
  status: ApprovalStatus;
  toolName: string;
  toolArgs: Record<string, unknown>;
  agentId?: string;
  agentName?: string;
  personaId?: string;
  executionContext: {
    orgId: string;
    triggeredBy: string;
  };
  createdAt: number;
  expiresAt: number;
  /** People who must approve. Absent on older requests: one. */
  minApprovals?: 1 | 2;
  approvals?: Array<{ uid: string; at: number; note?: string }>;
  reviewedAt?: number;
  reviewedBy?: string;
  reviewNote?: string;
  result?: {
    content: string;
    isError?: boolean;
  };
}

export async function listAgentApprovals(
  idToken: string,
  orgId: string,
  opts: { status?: ApprovalStatus; limit?: number } = {},
): Promise<Approval[]> {
  const url = new URL(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/agent-approvals`,
  );
  if (opts.status) url.searchParams.set('status', opts.status);
  if (opts.limit !== undefined) url.searchParams.set('limit', String(opts.limit));
  const res = await fetch(url.toString(), {
    headers: { authorization: `Bearer ${idToken}` },
  });
  const body = await jsonOrThrow(res);
  return body.approvals ?? [];
}

export async function approveAgentApproval(
  idToken: string,
  orgId: string,
  id: string,
  reason?: string,
): Promise<{
  ok: boolean;
  /** `pending` means this approval was recorded and another is needed. */
  status?: ApprovalStatus;
  result?: { content: string; isError?: boolean };
}> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/agent-approvals/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  return jsonOrThrow(res);
}

export async function denyAgentApproval(
  idToken: string,
  orgId: string,
  id: string,
  reason?: string,
): Promise<{ ok: boolean }> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/agent-approvals/${encodeURIComponent(id)}/deny`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  return jsonOrThrow(res);
}
